import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["list", "rightPanel"];
  static values = { moreLabel: { type: String, default: "…" } };

  connect() {
    this._onSelectChange = ({ target }) => {
      if (target.value) Turbo.visit(target.value);
    };
    this._select = this._createSelect();
    this._wrapper = document.createElement("div");
    this._wrapper.className = "nav-overflow-select-wrapper";
    this._wrapper.style.display = "none";
    this._wrapper.appendChild(this._select);
    this.rightPanelTarget.prepend(this._wrapper);

    this._observer = new ResizeObserver(() => this._update());
    this._observer.observe(this.element);
    this._update();
  }

  disconnect() {
    this._observer?.disconnect();
    this._select?.removeEventListener("change", this._onSelectChange);
    this._wrapper?.remove();
  }

  _createSelect() {
    const select = document.createElement("select");
    select.className =
      "nav-overflow-select btn react-dropdownbtn btn-secondary";
    select.addEventListener("change", this._onSelectChange);
    return select;
  }

  _update() {
    const items = Array.from(this.listTarget.querySelectorAll("li.nav-item"));

    if (items.length === 0) return;

    // Reset layout-affecting classes before measuring
    this.element.classList.remove("all-tabs-overflowing");

    // Show all tabs to measure from a clean state
    items.forEach((item) => (item.style.display = ""));

    const selectWasVisible = this._wrapper.style.display !== "none";

    if (!selectWasVisible) {
      // No select yet — check if the last tab has reached the list edge
      const listRight = this.listTarget.getBoundingClientRect().right;
      if (items[items.length - 1].getBoundingClientRect().right < listRight) {
        return; // nothing overflows, leave select hidden
      }
      // Overflow detected — show select so the boundary tightens
      this._select.innerHTML = `<option>${this.moreLabelValue}</option>`;
      this._wrapper.style.display = "";
    }

    // With the select visible the list is narrower — use that as the boundary.
    // When the select was already showing this keeps the boundary tight on
    // every update, so a tab is moved the instant it touches the select.
    const strictRight = this.listTarget.getBoundingClientRect().right;

    const overflowing = [];
    for (let i = items.length - 1; i >= 0; i--) {
      if (items[i].getBoundingClientRect().right >= strictRight) {
        items[i].style.display = "none";
        overflowing.push(items[i]);
      } else {
        break;
      }
    }
    overflowing.reverse();

    if (overflowing.length === 0) {
      this._wrapper.style.display = "none";
      return;
    }

    this.element.classList.toggle(
      "all-tabs-overflowing",
      overflowing.length === items.length
    );
    this._fillSelect(overflowing, items);
  }

  _fillSelect(overflowing, items) {
    this._select.innerHTML = "";

    const activeItem = items.find((item) => item.classList.contains("active"));

    const activeIsHidden = activeItem && overflowing.includes(activeItem);

    const label = document.createElement("option");
    label.value = "";
    label.disabled = true;
    label.selected = true;
    label.textContent = activeIsHidden
      ? (activeItem.querySelector("a")?.textContent.trim() ?? "…")
      : this.moreLabelValue;
    this._select.appendChild(label);

    // Add navigable options for each overflow tab (skip the active one —
    // it is already shown as the label and you are already on that page)
    overflowing.forEach((item) => {
      if (item.classList.contains("active")) return;
      const link = item.querySelector("a");
      if (!link) return;
      const opt = document.createElement("option");
      opt.value = link.href;
      opt.textContent = link.textContent.trim();
      this._select.appendChild(opt);
    });
  }
}
